import * as angular from 'angular';
import * as $ from 'jquery';
import * as _ from 'lodash';

import { ComponentDefinition, Dashboard, Category, Group, Miniature, KeyValuePair, IValueViewModel, IBindable, ContextualField, PropertyType, DashboardPeriod } from '../../../models';
import { DashboardThumbnailWidget } from '../../../widgets';

import { IBindingCheckerService, IBindingResult, ICurrentApplicationService, IDashboardFactory, IDashboardService, ILoggerService } from '../../../services';

// Explicit import on purpose, does not work if imported from common/index
import { dashboardPeriods } from '../../../common/dashboardPeriods';
import { guidEmpty } from '../../../common';

import {
    Background,
    Documentation,
    GroupTypes,
    IsExportToChildApplication,
    Name,
    ShortComment,
} from '../../../common/helpers/contextual';
import { BindingIssuesModalConfig } from '../../../components/bindingIssuesModal/bindingIssuesModalConfig';
import { buildGroupList } from './buildGroupList';

class ModernDashboardContextualController implements angular.IComponentController {

    // Bound properties
    categories: Category[];
    dashboards: Dashboard[];
    groups: Group[] = [];
    selectedItem: DashboardThumbnailWidget;
    save: () => angular.IPromise<void>;

    // Local properties
    formController: angular.IFormController;
    miniatures: Miniature[];
    newCategories: KeyValuePair<string, string>[];
    properties: ContextualField[];

    private _originalReference: string;

    static $inject = [
        'dashboardFactory',
        'bindingCheckerService',
        'translatedLoggerService',
        'dashboardService',
        'currentApplicationService'
    ];

    constructor(
        private _dashboardFactory: IDashboardFactory,
        private _bindingCheckerService: IBindingCheckerService,
        private _loggerService: ILoggerService,
        private _dashboardService: IDashboardService,
        private _currentApplicationService: ICurrentApplicationService
    ) {
    }

    $onInit() {
        this.initMiniatureList();
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['categories'] && this.categories) {
            const prop = _.find(this.properties, p => p.modelName === 'categories');
            if (prop) {
                prop.extraData.options = this.categories
                    .map(c => {
                        return { key: c.guid, value: c.name };
                    });
            }
        }
        if (changesObj['selectedItem'] && this.selectedItem) {
            this.newCategories = [];
            const referenceGroup = _.find(this.properties, p => p.modelName === 'referenceGroup');
            if (referenceGroup) {
                this.updateReferenceGroup(referenceGroup);
            }
            if (this.selectedItem && this.selectedItem.dashboard) {
                this._originalReference = this.selectedItem.dashboard.referenceGroup;
            } else if (this.selectedItem && !this.selectedItem.dashboard) {
                this._originalReference = this.selectedItem.referenceGroup;
            }
            if (this.selectedItem?.dashboard) {
                this.initProperties();
            }
        }
    }

    private initProperties(): void {
        const referenceGroup: ContextualField = {
            modelName: 'referenceGroup',
            label: 'Reference group',
            icon: 'fa-code-fork fa-rotate-180',
            propertyName: 'referenceGroupProperty',
            propertyType: PropertyType.Select,
            messages: [
                { key: 'required', value: 'A reference group is required' }
            ],
            isValid: (newValue: string) => {
                const ref = newValue;
                const valid = ref !== undefined
                    && ref !== guidEmpty
                    && referenceGroup.extraData.options.filter(g => g.key === ref).length > 0;
                return { 'required': valid };
            },
            extraData: {
            }
        };
        const groupTypes = angular.copy(GroupTypes);
        groupTypes.icon = ['fa-sitemap', 'fa-neuter'];
        groupTypes.extraData.onSelect = (item: string) => {
            this.updateReferenceGroup(referenceGroup, item);
        };
        groupTypes.extraData.onRemove = (item: string) => {
            this.updateReferenceGroup(referenceGroup, null, item);
        };
        this.updateReferenceGroup(referenceGroup);
        const categories = {
            modelName: 'categories',
            label: 'Categories',
            icon: 'fa-key',
            placeholder: 'Enter a category',
            propertyName: 'categoriesProperty',
            propertyType: PropertyType.MultiCategory,
            extraData: {
                options: this.categories
                    .map(c => {
                        return { key: c.guid, value: c.name };
                    }),
                onSelect: (item: KeyValuePair<string, string>) => {
                    if (this.categories.filter(c => c.guid === item.key).length === 0) {
                        this.newCategories.push(item);
                    }
                }
            }
        };
        const name = angular.copy(Name);
        name.isValid = (item: string) => {
            const isDuplicateValid = this.dashboards.filter(d => d.name === item && d.guid !== this.selectedItem.dashboard.guid).length === 0;
            return { 'duplicate': isDuplicateValid };
        };
        const background = angular.copy(Background);
        background.extraData.target = '#backgroundPickerModal';
        this.properties = [
            name,
            angular.copy(ShortComment),
            {
                modelName: 'defaultPeriod',
                label: 'Default period',
                icon: 'fa-arrows-h',
                propertyName: 'defaultPeriodProperty',
                propertyType: PropertyType.Select,
                extraData: {
                    options: angular.copy(dashboardPeriods)
                },
                onChange: (defaultPeriod) => this.setDatePickerDisabled(defaultPeriod, this.selectedItem?.dashboard)
            },
            {
                modelName: 'isDatePickerDisabled',
                label: 'Disable date picker',
                icon: ['fa-lock', 'fa-calendar'],
                propertyName: 'isDatePickerDisabledProperty',
                propertyType: PropertyType.Boolean,
                hide: !this.isDatePickerPropertyVisible(this.selectedItem?.dashboard?.defaultPeriod)
            },
            groupTypes,
            {
                modelName: 'groupGuids',
                propertyType: PropertyType.MultiGroup,
                label: 'Group names',
                propertyName: 'groupGuidsProperty',
                required: false,
                icon: ['fa-sitemap', 'fa-id-badge'],
                extraData: {
                    onSelect: () => { this.updateReferenceGroup(referenceGroup); },
                    onRemove: () => { this.updateReferenceGroup(referenceGroup); }
                },

            },
            referenceGroup,
            categories,
            background,
            {
                modelName: 'isHomePage',
                label: 'Home page',
                icon: 'fa-home',
                propertyName: 'isHomePageProperty',
                propertyType: PropertyType.Boolean,
                extraData: {}
            },
            this.getIsExportToChildApplication(),
            {
                modelName: 'isAutoRefresh',
                label: 'Start Auto-Refresh on open',
                icon: 'fa-play',
                propertyName: 'isAutoRefreshProperty',
                propertyType: PropertyType.Boolean,
                extraData: {}
            },
            Object.assign({}, Documentation)
        ];
    }

    private setDatePickerDisabled(defaultPeriod: DashboardPeriod, currentDashboard: Dashboard): void {
        const isDatePickerVisible = this.isDatePickerPropertyVisible(defaultPeriod);
        if (!isDatePickerVisible && currentDashboard.isDatePickerDisabled) {
            currentDashboard.isDatePickerDisabled = false;
        }
        if (this.properties) {
            const foundProperty = this.properties.find(property => property.modelName === 'isDatePickerDisabled');
            if (foundProperty) {
                foundProperty.hide = !isDatePickerVisible;
            }
        }
    }

    private isDatePickerPropertyVisible(defaultPeriod: DashboardPeriod): boolean {
        return !!defaultPeriod
            && (defaultPeriod == DashboardPeriod.Today
                || defaultPeriod == DashboardPeriod.Yesterday
                || defaultPeriod == DashboardPeriod.LastWeek
                || defaultPeriod == DashboardPeriod.ThisWeek
                || defaultPeriod == DashboardPeriod.LastMonth
                || defaultPeriod == DashboardPeriod.ThisMonth
                || defaultPeriod == DashboardPeriod.NextMonth
                || defaultPeriod == DashboardPeriod.LastQuarter
                || defaultPeriod == DashboardPeriod.ThisQuarter
                || defaultPeriod == DashboardPeriod.NextQuarter
                || defaultPeriod == DashboardPeriod.LastYear
                || defaultPeriod == DashboardPeriod.ThisYear
                || defaultPeriod == DashboardPeriod.NextYear);
    }

    private getIsExportToChildApplication(): ContextualField {
        const isChildAllowed = this._currentApplicationService.getApplicationConfiguration().isChildApplicationCreationAllowed;
        const result = angular.copy(IsExportToChildApplication);
        result.hide = !isChildAllowed;
        return result;
    }

    private updateReferenceGroup(referenceGroup: ContextualField, newType?: string, toRemove?: string) {
        if (referenceGroup) {
            const oldOptions = referenceGroup.extraData.options;
            const refGroups = this.getReferenceGroups(newType, toRemove);
            referenceGroup.extraData.options = refGroups;
            referenceGroup.disabled = !refGroups || !refGroups.length;
            referenceGroup.required = !referenceGroup.disabled;
            if (this.selectedItem && this.selectedItem.dashboard) {
                this.selectedItem.dashboard.referenceGroup = !oldOptions?.length && refGroups?.length && !refGroups.filter(d => d.key === this.selectedItem.dashboard.referenceGroup).length
                    ? null
                    : this.selectedItem.dashboard.referenceGroup;
            }
        }
    }

    private getReferenceGroups(newValue?: string, toRemove?: string): { key: string, value: string }[] {
        let types = [];
        if (this.selectedItem && this.selectedItem.dashboard && this.selectedItem.dashboard.groupTypes) {
            types = this.selectedItem?.dashboard.groupTypes.map(d => d) || [];
        }
        if (newValue && types.indexOf(newValue) < 0) {
            types.push(newValue);
        }
        if (toRemove) {
            types = types.filter(t => t !== toRemove);
        }

        let groupGuids = [];
        if (this.selectedItem
            && this.selectedItem.dashboard
            && this.selectedItem.dashboard.groupGuids
            && this.selectedItem.dashboard.groupGuids.length) {
            groupGuids = this.selectedItem.dashboard.groupGuids;
        }

        return buildGroupList({ types, groupGuids }, this.groups);
    }

    private initMiniatureList(): void {
        const dashboardTypes = this._dashboardFactory.getMiniatureList();
        dashboardTypes.forEach(m => (m as any).data = { miniature: true });
        this.miniatures = dashboardTypes;
    }


    /**********/
    // Modal for binding to new reference group
    modalConfig: BindingIssuesModalConfig;
    /**
     * Save the dashboard or check its bindings
     */
    onSave() {
        const field = this.formController['referenceGroupProperty'];
        if (field && field.$dirty && this._originalReference !== this.selectedItem.dashboard.referenceGroup) {
            return this.checkBindings();
        } else {
            return this.save();
        }
    }

    /**
     * Check the bindings
     */
    private checkBindings() {
        const { dashboard } = this.selectedItem;
        const type = typeof ('');
        const deserializedDashboard = dashboard.configuration.widgets.filter(w => typeof (w.configuration) === type).length > 0
            ? this._dashboardService.deserializeDashboard(dashboard)
            : dashboard;
        return this._bindingCheckerService
            .checkBindings(this.selectedItem.dashboard.referenceGroup, this._originalReference, deserializedDashboard.configuration.widgets)
            .then(data => {
                const { outside, bindResult, seriesByWidget } = data;
                this.selectedItem.dashboard = deserializedDashboard;
                if (outside.list.length > 0) {
                    this.createModal(outside, bindResult, seriesByWidget);
                    return true;
                } else {
                    this._bindingCheckerService.remapBoundSeries(seriesByWidget, bindResult);
                    return this.save()
                        .then(() => false);
                }
            })
            .catch(reason => {
                this._loggerService.logError('Unable to rebind series to new reference group.');
                console.log(reason);
            });
    }

    /**
     * Series mapping confirmed => call Save
     */
    modalSeriesMapConfirm() {
        this._bindingCheckerService.remapBoundSeries(this.modalConfig.seriesByWidget, this.modalConfig.bindResult);
        this._originalReference = this.selectedItem.dashboard.referenceGroup;
        this.formController.$setPristine();
        return this.save();
    }

    /**
     * Create modal box for save confirmation
     * @param outside
     * @param bindResult
     * @param seriesByWidget
     */
    private createModal(outside: { reference: Group; list: IValueViewModel[] },
        bindResult: IBindingResult,
        seriesByWidget: { [guid: string]: IBindable[]; }) {
        this.modalConfig = this._bindingCheckerService.createModal({ outside, bindResult, seriesByWidget },
            this.selectedItem.dashboard.configuration.widgets);

        $('#modalSeriesMapConfirm').modal('show');
    }

}

export const wtModernDashboardContextual: ComponentDefinition = {
    componentId: 'wtModernDashboardContextual',
    controller: ModernDashboardContextualController,
    template: require('./modernDashboardContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        formController: '=?',
        newCategories: '=?',
        selectedItem: '<',
        categories: '<',
        groups: '<',
        dashboards: '<',
        save: '&',
        onCancel: '&?'
    }
};